// Types based on the backend schema

const BASE_URL = `${import.meta.env.VITE_BASE_URL}/hp`;

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('firebase-auth-token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        ...options,
        headers: { ...getAuthHeaders(), ...(options?.headers || {}) },
        credentials: 'include',
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const err: any = new Error(errData.message || `Request failed (${res.status})`);
        err.response = { json: async () => errData };
        err.data = errData;
        throw err;
    }
    return res.json();
}

// ─── ID Resolution Workaround ────────────────────────────────
// Maps cohort name to the real database IDs used in the enrollment collection.
// This allows the frontend to resolve placeholder IDs ("000...1") before API calls.
export const COHORT_ID_MAP: Record<string, { courseId: string; versionId: string; cohortId: string }> = {
    Euclideans: { courseId: "6968e12cbf2860d6e39051ae", versionId: "6968e12cbf2860d6e39051af", cohortId: "69d31909b50379d839ff3492" },
    Dijkstrians: { courseId: "6970f87e30644cbc74b6714f", versionId: "6970f87e30644cbc74b67150", cohortId: "69d3190ab50379d839ff3493" },
    Kruskalians: { courseId: "697b4e262942654879011c56", versionId: "697b4e262942654879011c57", cohortId: "69d3190ab50379d839ff3494" },
    RSAians: { courseId: "69903415e1930c015760a718", versionId: "69903415e1930c015760a719", cohortId: "69d3190ab50379d839ff3495" },
    AKSians: { courseId: "69942dc6d6d99b252e3a54fe", versionId: "69942dc6d6d99b252e3a54ff", cohortId: "69d3190ab50379d839ff3496" },
    A: { courseId: "69d2b1bc0744872b91ab54d9", versionId: "69d2b1bc0744872b91ab54da", cohortId: "69d31a4697aaee1324e583d5" },
    B: { courseId: "69d2b2e50744872b91ab641e", versionId: "69d2b2e50744872b91ab641f", cohortId: "69d31a4697aaee1324e583d6" },
    Scorchers: { courseId: "69c77763b4ae917c56cf1342", versionId: "69c77763b4ae917c56cf1343", cohortId: "69d3190ab50379d839ff3498" },
    Testians: { courseId: "69c77812b4ae917c56cf227e", versionId: "69c77812b4ae917c56cf227f", cohortId: "69d3190ab50379d839ff3497" },
};

export function getEffectiveIds(cohortName: string, fallbackCourseId: string, fallbackVersionId: string) {
    const mapping = COHORT_ID_MAP[cohortName];
    return {
        courseId: mapping?.courseId ?? fallbackCourseId,
        courseVersionId: mapping?.versionId ?? fallbackVersionId,
        cohortId: mapping?.cohortId ?? cohortName,
    };
}

// ─── Interfaces ──────────────────────────────────────────────

export interface CourseVersionStats {
    courseVersionId: string;
    versionName: string;
    totalCohorts: number;
    createdAt?: string;
    /** False when the HP System was switched off but the version still holds HP data. */
    hpEnabled?: boolean;
}

export interface CourseWithVersions {
    courseId: string;
    courseName: string;
    versions: CourseVersionStats[];
}

export interface CohortStats {
    cohortId: string;
    cohortName: string;
    courseVersionId: string;
    stats: {
        totalStudents: number;
        totalActivities: number;
        publishedActivities: number;
        draftActivities: number;
        totalHpDistributed: number;
        totalCredits: number;
        totalDebits: number;
        pendingApprovals: number;
        overdueActivities: number;
    };
    lastActivityAt: string;
    createdAt?: string;
}

export interface HpActivity {
    _id: string;
    courseVersionId: string;
    courseId: string;
    cohortId: string;
    createdByTeacherId?: string;
    publishedByTeacherId?: string;
    instructorName?: string;
    status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
    title: string;
    description: string;
    activityType: "ASSIGNMENT" | "MILESTONE" | "EXTERNAL_IMPORT" | "VIBE_MILESTONE" | "OTHER";
    submissionMode: "IN_PLATFORM" | "EXTERNAL_LINK";
    externalLink?: string;
    // required_percentage?: number;
    attachments?: {
        name: string;
        url: string;
        kind: 'PDF' | 'LINK' | 'OTHER';
    }[];
    stats?: {
        totalStudents: number;
        submittedCount: number;
        completedCount: number;
        overdueCount: number;
        lastRecomputedAt: string;
    };
    rules?: {
        isMandatory: boolean;
        deadlineAt: Date | string;
        allowLateSubmission: boolean;
    };
    required_percentage?: number;
    createdAt: string;
    updatedAt: string;
    isSubmitted?: boolean;
}

export interface HpStudent {
    _id: string;
    name: string;
    email: string;
    totalHp: number;
    completionPercentage: number;
    isSafe: boolean;
    avatar?: string;
}

export interface HpLedgerCalcEntry {
    ruleType?: string;
    percentage?: number | null;
    absolutePoints?: number;
    baseHpAtTime?: number;
    computedAmount?: number;
    deadlineAt?: string;
    withinDeadline?: boolean;
    reasonCode?: string;
}

export interface HpLedgerMetaEntry {
    triggeredBy?: string;
    triggeredByUserId?: string;
    triggeredByUserName?: string | null;
    note?: string;
}

export interface HpLedgerLinksEntry {
    reversedLedgerId?: string;
    relatedLedgerIds?: string[];
}

export interface HpLedgerTransformerEntry {
    _id: string;
    courseId?: string;
    courseVersionId?: string;
    cohort?: string;
    studentId: string;
    studentEmail?: string;
    activityId?: string;
    submissionId?: string;
    eventType: string;
    direction: 'CREDIT' | 'DEBIT';
    amount: number;
    calc?: HpLedgerCalcEntry | null;
    links?: HpLedgerLinksEntry | null;
    meta?: HpLedgerMetaEntry | null;
    createdAt: string;
}

export interface LedgerStudentDetails {
    studentName: string;
    studentEmail: string;
    hpPoints: number;
}

export interface LedgerListResponse {
    data: HpLedgerTransformerEntry[];
    studentDetails: LedgerStudentDetails;
    total: number;
    page: number;
    limit: number;
}

export interface StudentDashboardStats {
    myStats: {
        totalHp: number;
        completedActivities: number;
        pendingSubmissions: number;
        completionPercentage: number;
    };
    progressTimeline: {
        date: string;
        hpChange: number;
        activitiesCompleted: number;
    }[];
    activityBreakdown: {
        notStarted: number;
        submitted: number;
        approved: number;
        rejected: number;
    };
    upcomingDeadlines: {
        activityTitle: string;
        deadlineDate: string;
        daysLeft: number;
    }[];
    recentSubmissions: {
        activityTitle: string;
        submittedAt: string;
        status: string;
        hpEarned?: number;
    }[];
}

export interface HpCohortOverviewStats {
    totalActivities: number;
    totalSubmissions: number;
    totalPendings: number;
    totalLateSubmissions: number;
    currentHp: number;
    reward?: {
        type: string;
        value: number;
    } | null;
    totalStudents: number;
    bestPerformingCohort: string;
    coursePerformance: {
        courseId: string;
        courseName: string;
        totalHp: number;
        studentCount: number;
    }[];
    weeklyActivity: {
        date: string;
        submitted: number;
        approved: number;
        rejected: number;
    }[];
    // Add missing fields that the component expects
    completionRates?: {
        activityId: string;
        activityTitle: string;
        submittedCount: number;
        pendingCount: number;
        revertedCount: number;
        totalAssigned: number;
    }[];
    hpDistribution?: {
        range: string;
        count: number;
        percentage: number;
    }[];
    studentProgress?: {
        completed: number;
        inProgress: number;
        notStarted: number;
    }[];
    studentPerformance?: {
        studentId: string;
        name: string;
        email: string;
        completedActivities: number;
        hpBalance: number;
        completionPercentage: number;
        lastActivityDate: string;
        status: 'on-track' | 'at-risk' | 'inactive';
    }[];
}

export interface SubmissionAttachment {
    _id: string;
    name: string;
    url: string;
    type: 'image' | 'pdf' | 'document' | 'link' | 'other';
    textResponse?: string;
    files?: Array<{ _id: string; name: string; url: string; type: string }>;
    images?: Array<{ _id: string; name: string; url: string; type: string }>;
    links?: Array<{ _id: string; name: string; url: string; type: string }>;
}

export interface InstructorFeedback {
    decision?: string;
    reviewerName?: string;
    reviewerEmail?: string;
    reviewedAt?: string;
    note?: string;
}

export interface HpStudentSubmission {
    _id: string;
    activityId: string;
    activityTitle: string;
    activityDescription?: string;
    status: 'SUBMITTED' | 'PENDING' | 'REVERTED' | 'APPROVED' | 'REJECTED';
    attachments: SubmissionAttachment[];
    submissionLink?: string;
    dueDate?: string;
    submittedAt?: string;
    lastUpdated?: string;
    submissionCount: number;
    isLate: boolean;
    baseHp: number;
    currentHp: number;
    instructorFeedback?: {
        decision: string;
        note: string;
        reviewedAt: string;
        reviewedBy: string;
    };
    feedbacks?: Array<{
        feedback: string;
        feedbackAt: string;
        username: string;
        email: string;
    }>;
    submission?: {
        _id: string;
        status: string;
        submittedAt: string;
        attachments?: {
            textResponse?: string;
            files?: Array<{ _id: string; name: string; url: string; type: string }>;
            images?: Array<{ _id: string; name: string; url: string; type: string }>;
            links?: Array<{ _id: string; name: string; url: string; type: string }>;
        };
    };
    safetyStatus?: 'safe' | 'unsafe';
    isRequiredInstructorApproval?: boolean;
}

export enum SubmissionField {
    TEXT = "TEXT",
    PDF = "PDF",
    IMAGE = "IMAGE",
    URL = "URL",
}

export interface HpRuleConfig {
    _id: string;
    courseId: string;
    courseVersionId: string;
    activityId: string;
    isMandatory: boolean;
    submissionValidation:SubmissionField[];
    deadlineAt?: string;
    allowLateSubmission?: boolean;
    reward?: {
        enabled: boolean;
        type: "ABSOLUTE" | "PERCENTAGE";
        value: number;
        applyWhen: "ON_SUBMISSION" | "ON_APPROVAL";
        lateBehavior: "NO_REWARD" | "REWARD";
    };
    penalty?: {
        enabled: boolean;
        type: "ABSOLUTE" | "PERCENTAGE";
        value: number;
        applyWhen: "AFTER_DEADLINE";
        graceMinutes: number;
        runOnce: boolean;
    };
    limits?: {
        minHp?: number;
        maxHp?: number;
    };
    createdAt?: string;
    updatedAt?: string;
}

export interface HpStudentActivity {
    _id: string;
    courseVersionId: string;
    courseId: string;
    cohortId: string;
    title: string;
    description: string;
    activityType: "ASSIGNMENT" | "MILESTONE" | "EXTERNAL_IMPORT" | "VIBE_MILESTONE" | "OTHER";
    submissionMode: "IN_PLATFORM" | "EXTERNAL_LINK";
    externalLink?: string;
    attachments?: {
        name: string;
        url: string;
        kind: 'PDF' | 'LINK' | 'OTHER';
    }[];
    // Rule details embedded for student view
    isMandatory: boolean;
    deadlineAt?: string;
    allowLateSubmission?: boolean;
    rewardType?: "ABSOLUTE" | "PERCENTAGE";
    rewardValue?: number;
    studentStatus: "NOT_STARTED" | "SUBMITTED" | "GRADED" | "OVERDUE";
}

export interface CreateHpActivityPayload {
    courseId: string;
    courseVersionId: string;
    cohortId: string;
    title: string;
    description: string;
    activityType: string;
    submissionMode: string;
    externalLink?: string;
    status?: "DRAFT" | "PUBLISHED";
    deadlineAt?: string;
    allowLateSubmission?: boolean;
    attachments?: { name: string; url: string; kind: string }[];
    required_percentage?: number;
}

export interface HpStudentSubmissionStats {
    totalActivities: number;
    totalSubmissions: number;
    totalPendings: number;
    totalLateSubmissions: number;
    currentHp: number;
    reward?: {
        type: "ABSOLUTE" | "PERCENTAGE";
        value: number;
    } | null;
}
export type HpCohortsResponse = {
    success: boolean;
    message: string;
    data: CohortStats[];
    courseVersionName: string;
};

export type ResetHpPayload = {
  courseVersionId: string;
  cohortName: string;
  mode: "ALL" | "ONLY_ZERO_HP" | "ONLY_WITH_HP";
  targetHp: number;
};

export type ResetStudentHpPayload = {
  courseVersionId: string;
  studentId: string;
  cohortName: string;
  targetHp: number;
}
// ─── API Functions ───────────────────────────────────────────

export const hpApi = {

    // ── Courses & Cohorts (mock backend) ──────────────────────
    
    getCourseVersions: async (): Promise<{ success: boolean; data: CourseWithVersions[] }> => {
        return apiFetch(`${BASE_URL}/courses-cohorts/courses/versions`);
    },

    getCourseDetails: async (versionId: string) => {
    return apiFetch(`${BASE_URL}/courses-cohorts/course/${versionId}/details`);
},

    // getCohorts: async (courseVersionId: string): Promise<{ success: boolean; message: string; data: CohortStats[] }> => {
    getCohorts: async (courseVersionId: string): Promise<HpCohortsResponse> => {
        const params = new URLSearchParams({ courseVersionId });
        return apiFetch(`${BASE_URL}/courses-cohorts/cohorts?${params.toString()}`);
    },

    getStudentCohorts: async (): Promise<{ success: boolean; message: string; data: any[]; totalHp?: number }> => {
        return apiFetch(`${BASE_URL}/courses-cohorts/student-cohorts`);
    },

    getStudentActivities: async (
        courseVersionId: string,
        cohortId: string
    ): Promise<{ success: boolean; data: HpActivity[] }> => {
        // Use the existing activities endpoint, filtered to PUBLISHED only for students
        const params = new URLSearchParams({ courseVersionId, cohortId: cohortId, status: 'PUBLISHED' });
        return apiFetch(`${BASE_URL}/activities?${params.toString()}`);
    },

    submitActivity: async (options: {
        courseId: string;
        courseVersionId: string;
        cohortId: string;
        activityId: string;
        payload: {
            textResponse?: string;
            links?: { url: string; label: string }[];
        };
        submissionSource?: string;
        files?: File[];
        images?: File[];
    }): Promise<{ success: boolean; data: any }> => {
        const { files, images, ...rest } = options;
        const hasFiles = (files && files.length > 0) || (images && images.length > 0);

        if (hasFiles) {
            const formData = new FormData();
            formData.append('courseId', rest.courseId);
            formData.append('courseVersionId', rest.courseVersionId);
            formData.append('cohortId', rest.cohortId);
            formData.append('activityId', rest.activityId);
            if (rest.submissionSource) {
                formData.append('submissionSource', rest.submissionSource);
            }
            // flatten payload
            if (rest.payload.textResponse) {
                formData.append('payload[textResponse]', rest.payload.textResponse);
            }
            if (rest.payload.links) {
                rest.payload.links.forEach((link, idx) => {
                    formData.append(`payload[links][${idx}][url]`, link.url);
                    formData.append(`payload[links][${idx}][label]`, link.label);
                });
            }

            if (files) {
                files.forEach(f => formData.append('files', f));
            }
            if (images) {
                images.forEach(img => formData.append('images', img));
            }

            const token = localStorage.getItem('firebase-auth-token');
            const res = await fetch(`${BASE_URL}/activity-submissions`, {
                method: 'POST',
                body: formData,
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: 'include',
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                const err: any = new Error(errData.message || `Request failed (${res.status})`);
                err.response = { json: async () => errData };
                err.data = errData;
                throw err;
            }
            return res.json();
        }

        return apiFetch(`${BASE_URL}/activity-submissions`, {
            method: 'POST',
            body: JSON.stringify(rest),
        });
    },

    updateActivitySubmission: async (submissionId: string, options: {
        courseId: string;
        courseVersionId: string;
        cohortId: string;
        activityId: string;
        payload: {
            textResponse?: string;
            links?: { url: string; label: string }[];
            files?: any[];
            images?: any[];
        };
        submissionSource?: string;
        files?: File[];
        images?: File[];
    }): Promise<{ success: boolean; data: any }> => {
        const { files, images, ...rest } = options;
        const hasFiles = (files && files.length > 0) || (images && images.length > 0);

        if (hasFiles) {
            const formData = new FormData();
            formData.append('courseId', rest.courseId);
            formData.append('courseVersionId', rest.courseVersionId);
            formData.append('cohortId', rest.cohortId);
            formData.append('activityId', rest.activityId);
            if (rest.submissionSource) {
                formData.append('submissionSource', rest.submissionSource);
            }
            if (rest.payload.textResponse) {
                formData.append('payload[textResponse]', rest.payload.textResponse);
            }
            if (rest.payload.links) {
                rest.payload.links.forEach((link, idx) => {
                    formData.append(`payload[links][${idx}][url]`, link.url);
                    formData.append(`payload[links][${idx}][label]`, link.label);
                });
            }
            if (rest.payload.files) {
                rest.payload.files.forEach((file: any, idx: number) => {
                    if (file.fileId) formData.append(`payload[files][${idx}][fileId]`, file.fileId);
                    if (file.url) formData.append(`payload[files][${idx}][url]`, file.url);
                    if (file.name) formData.append(`payload[files][${idx}][name]`, file.name);
                    if (file.mimeType) formData.append(`payload[files][${idx}][mimeType]`, file.mimeType);
                    if (file.sizeBytes !== undefined) formData.append(`payload[files][${idx}][sizeBytes]`, String(file.sizeBytes));
                });
            }
            if (rest.payload.images) {
                rest.payload.images.forEach((img: any, idx: number) => {
                    if (img.fileId) formData.append(`payload[images][${idx}][fileId]`, img.fileId);
                    if (img.url) formData.append(`payload[images][${idx}][url]`, img.url);
                    if (img.name) formData.append(`payload[images][${idx}][name]`, img.name);
                });
            }

            if (files) {
                files.forEach(f => formData.append('files', f));
            }
            if (images) {
                images.forEach(img => formData.append('images', img));
            }

            const token = localStorage.getItem('firebase-auth-token');
            const res = await fetch(`${BASE_URL}/activity-submissions/${submissionId}`, {
                method: 'PUT',
                body: formData,
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                credentials: 'include',
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                const err: any = new Error(errData.message || `Request failed (${res.status})`);
                err.response = { json: async () => errData };
                err.data = errData;
                throw err;
            }
            return res.json();
        }

        return apiFetch(`${BASE_URL}/activity-submissions/${submissionId}`, {
            method: 'PUT',
            body: JSON.stringify(rest),
        });
    },

    // ── Activities (real backend) ────────────────────────────

    getActivities: async (
        courseVersionId: string,
        cohortId: string,
        status?: string,
        search?: string,
        activity?: string
    ): Promise<{ success: boolean; data: HpActivity[] }> => {
        const params = new URLSearchParams({ courseVersionId, cohortId });
        if (status && status !== 'ALL') params.append('status', status);
        if (search) params.append('search', search);
        if (activity && activity !== 'ALL') params.append('activity', activity);
        return apiFetch(`${BASE_URL}/activities?${params.toString()}`);
    },

    getStudentMySubmissions: async (
        courseVersionId: string,
        cohortId: string
    ): Promise<{ success: boolean; data: any[] }> => {
        const params = new URLSearchParams({ courseVersionId, cohortId });
        // The teacher list endpoint returns the submissions according to the query.
        // It relies on the token if we had a specific student endpoint, but we can reuse the generic list one 
        // if the backend filters it by the token's user ID.
        return apiFetch(`${BASE_URL}/activity-submissions/student/my-submissions?${params.toString()}`);
    },

    createActivity: async (payload: CreateHpActivityPayload): Promise<{ success: boolean; data: HpActivity }> => {
        return apiFetch(`${BASE_URL}/activities`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    updateActivity: async (activityId: string, updates: Partial<HpActivity>): Promise<{ success: boolean; data: HpActivity }> => {
        return apiFetch(`${BASE_URL}/activities/${activityId}`, {
            method: 'PATCH',
            body: JSON.stringify(updates),
        });
    },

    publishActivity: async (activityId: string): Promise<{ success: boolean; data: HpActivity }> => {
        return apiFetch(`${BASE_URL}/activities/${activityId}/publish`, {
            method: 'POST',
        });
    },

    archiveActivity: async (activityId: string): Promise<{ success: boolean; data: HpActivity }> => {
        return apiFetch(`${BASE_URL}/activities/${activityId}/archive`, {
            method: 'POST',
        });
    },

    getActivityById: async (activityId: string): Promise<{ success: boolean; data: HpActivity }> => {
        return apiFetch(`${BASE_URL}/activities/${activityId}`);
    },

    deleteActivity: async (activityId: string): Promise<{ success: boolean }> => {
        // Backend does not have a DELETE endpoint; archive instead
        return apiFetch(`${BASE_URL}/activities/${activityId}/delete`, {
            method: 'POST',
        });
    },

    createActivityWithRule: async (payload: { activity: CreateHpActivityPayload; ruleConfig: Partial<HpRuleConfig>; }): Promise<{ success: boolean; data: HpRuleConfig }> => {
        return apiFetch(`${BASE_URL}/activities/with-rule`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    // ── Rule Configs (real backend) ──────────────────────────

    getRuleConfigByActivityId: async (activityId: string): Promise<{ success: boolean; data: HpRuleConfig | null }> => {
        return apiFetch(`${BASE_URL}/rule-config/activity/${activityId}`);
    },

    getRuleConfigById: async (ruleConfigId: string): Promise<{ success: boolean; data: HpRuleConfig }> => {
        return apiFetch(`${BASE_URL}/rule-config/${ruleConfigId}`);
    },

    createRuleConfig: async (payload: Partial<HpRuleConfig>): Promise<{ success: boolean; data: HpRuleConfig }> => {
        return apiFetch(`${BASE_URL}/rule-config`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    },

    updateRuleConfig: async (ruleConfigId: string, updates: Partial<HpRuleConfig>): Promise<{ success: boolean; data: HpRuleConfig }> => {
        return apiFetch(`${BASE_URL}/rule-config/${ruleConfigId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    },

    // ── Legacy compatibility shim (kept for RuleSettingsDialog) ──
    getRuleConfigs: async (_courseVersionId: string, _cohortId: string): Promise<{ success: boolean; data: HpRuleConfig[] }> => {
        // No list-all endpoint on backend; return empty for now
        return { success: true, data: [] };
    },

    // ── Students & Ledger (mock data) ────────────────────────

    getStudents: async (
        courseVersionId: string,
        cohortId: string,
        params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: "ALL" | "SAFE" | "UNSAFE";
    }
    ): Promise<{ success: boolean; data: HpStudent[] }> => {
        // Pass a large limit so we get all students for client-side pagination 
        const { page = 1, limit = 10000, search = "", status = "ALL" } = params || {};
        return apiFetch(`${BASE_URL}/courses-cohorts/version/${courseVersionId}/cohort/${cohortId}/students?limit=${limit}&page=${page}&search=${search}&status=${status}`);
    },

    getCohortOverviewStats: async (
        courseVersionId: string,
        cohortId: string,
    ): Promise<{ success: boolean; data: HpCohortOverviewStats }> => {
        const response = await apiFetch(`${BASE_URL}/activity-submissions/stats/cohort/${cohortId}/courseversion/${courseVersionId}`);
        return response as { success: boolean; data: HpCohortOverviewStats };
    },

    getStudentDashboardStats: async (
        courseVersionId: string,
        cohortId: string,
        timelineDays: number = 7,
    ): Promise<{ success: boolean; data: StudentDashboardStats }> => {
        const params = new URLSearchParams({ 
            courseVersionId, 
            cohortName: cohortId, 
            timelineDays: String(timelineDays) 
        });
        return apiFetch(`${BASE_URL}/activity-submissions/student/dashboard-stats?${params.toString()}`);
    },

    getStudentSubmissionStats: async (
        studentId: string,
        cohortId: string,
    ): Promise<{ success: boolean; data: HpStudentSubmissionStats | null }> => {
        return apiFetch(
            `${BASE_URL}/activity-submissions/stats/student/${studentId}/cohort/${encodeURIComponent(cohortId)}`
        );
    },

    getStudentSubmissions: async (
        studentId: string,
        courseVersionId: string,
        cohortId: string,
        sortOrder: 'asc' | 'desc' = 'desc',
    ): Promise<{ success: boolean; data: any[] }> => {
        const params = new URLSearchParams({ courseVersionId, cohortId, sortOrder });
        // The backend returns { success, data: StudentActivitySubmissionsViewDto[] }
        return apiFetch(`${BASE_URL}/activity-submissions/student/${studentId}/cohort/${cohortId}?${params.toString()}`);
    },


    // TODO: Do not remove this function, later we will use it
    // getStudentSubmissionStats: async(
    //     studentId: string,
    //     cohortId: string,
    // ): Promise<{ success: boolean; data: any }> => {
    //     return apiFetch(`${BASE_URL}/activity-submissions/stats/student/${studentId}/cohort/${cohort}`);
    // },

    // Made by Rishabh Shukla
    getCohortActivityStats: async (cohortId: string, activityId: string): Promise<{ data: any }> => {
        return apiFetch(`${BASE_URL}/activity-submissions/stats/cohort/${cohortId}/activity/${activityId}`);
    },

    // Made by Rishabh Shukla
    getCohortActivityStatsMap: async (cohortId: string, courseVersionId: string): Promise<{ success: boolean; data: any }> => {
        return apiFetch(`${BASE_URL}/activity-submissions/stats/cohort/${cohortId}/courseversion/${courseVersionId}`);
    },

    getStudentLedger: async (
        studentId: string,
        cohortId: string,
        courseId: string,
        courseVersionId: string,
        page: number = 1,
        limit: number = 50,
    ): Promise<LedgerListResponse> => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        return apiFetch(
            `${BASE_URL}/ledger/student/${studentId}/cohort/${encodeURIComponent(cohortId)}/course/${courseId}/courseVersion/${courseVersionId}?${params.toString()}`
        );
    },

    getMyLedger: async (
        courseId: string,
        courseVersionId: string,
        cohortId: string,
        page: number = 1,
        limit: number = 50,
    ): Promise<LedgerListResponse> => {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        return apiFetch(
            `${BASE_URL}/ledger/student/my-ledger/course/${courseId}/courseVersion/${courseVersionId}/cohort/${encodeURIComponent(cohortId)}?${params.toString()}`
        );
    },

    revertLedgerEntry: async (entryId: string): Promise<{ success: boolean }> => {
        console.log('Mock revert for entry:', entryId);
        return { success: true };
    },

    restoreLedgerEntry: async (submissionId: string, note?: string): Promise<{ success: boolean }> => {
        return apiFetch(`${BASE_URL}/activity-submissions/${submissionId}/restore`, {
            method: 'POST',
            body: JSON.stringify({ note }),
        });
    },

    reviewSubmission: async (submissionId: string, decision: "APPROVED" | "REJECTED" | "REVERTED", note?: string, pointsToDeduct?: number): Promise<{ success: boolean; data: any }> => {
        return apiFetch(`${BASE_URL}/activity-submissions/${submissionId}/review`, {
            method: 'POST',
            body: JSON.stringify({ decision, note, pointsToDeduct }),
        });
    },
    addFeedback: async (submissionId: string, feedback: string): Promise<{ success: boolean; data: any }> => {
        return apiFetch(`${BASE_URL}/activity-submissions/${submissionId}/feedback`, {
            method: 'POST',
            body: JSON.stringify({ feedback }),
        });
    },

    resetHp: async ({courseVersionId,cohortName,targetHp,mode}:ResetHpPayload ): Promise<{success: boolean, documentsUpdated: number}> => {
        return apiFetch(`${BASE_URL}/courses-cohorts/version/${courseVersionId}/cohort/${cohortName}/reset-hp`, {
            method: 'POST',
            body:JSON.stringify({targetHp:targetHp,mode:mode})
        });
    },

    resetStudentHp: async ({courseVersionId,studentId,cohortName,targetHp}:ResetStudentHpPayload ): Promise<{success: boolean}> => {
        return apiFetch(`${BASE_URL}/courses-cohorts/version/${courseVersionId}/cohort/${cohortName}/student/${studentId}/reset-hp`, {
            method: 'POST',
            body:JSON.stringify({targetHp:targetHp})
        });
    }
};
